import * as React from "react";
import {
  Action,
  AsyncState,
  loadingAsyncState,
  stateful,
  any,
  async,
  nothing,
} from "widgets-for-react";
import { Page } from "../../cms/page/types.gen";
import { CustomBlock } from "../../wordpress/types_and_parser/custom_blocks.mod";
import { Block } from "../../wordpress/types_and_parser/standard_blocks.gen";
import { PublicSite_CustomPageData } from "../application.mod";
import { getImageAttribute, placeholderImage } from "../utils/extra_images.mod";
import { link, linkWrapper } from "../../cms/blocks/utils.gen";
import { decodeHTMLEntities } from "../../../helpers";
import { WordPress } from "../../wordpress/wordpress_api";
import { Link as LinkType } from "../../cms/blocks/types.gen";
import { fromJSX } from "../../../utils";
import i18next from "i18next";
import { Attributes } from "../custom_pages/Webinars/webinars.types";

export type RelatedArticlesBlockItem = {
  title: string;
  link: LinkType;
  image?: WordPress.MediaResponse;
};

export type RelatedArticlesBlockStyle = Partial<{
  block_classname: string;
  inner_classname: string;
  header_classname: string;
  title_classname: string;
  content_classname: string;
  intro_classname: string;
  visual_classname: string;
}>;

export const relatedArticles = (
  page: Page<CustomBlock, Block, PublicSite_CustomPageData>
) =>
  stateful<AsyncState<Array<RelatedArticlesBlockItem>>>()((state) =>
    any<Action<AsyncState<Array<RelatedArticlesBlockItem>>>>()([
      async<Array<RelatedArticlesBlockItem>>()(state),
      state.kind == "loaded" && state.value.length > 0
        ? fromJSX(() => {
            return (
              <section className={`block block--grid block--grid--image'}`}>
                <div className="block__inner container container--m gutter--m spacing--m">
                  <header className="block__header block__header--spacing block__header--columns">
                    <h2 className="block__title typo--l ff--primary ls-second-item">
                      {i18next.t("related_articles.title")}
                    </h2>
                  </header>
                  <div className="block__content">
                    <ul className="block__grid grid ls-columns">
                      {state.value.map((article) => {
                        const gridItemInner: JSX.Element = (
                          <>
                            <div className="grid__content ls-bottom-item">
                              {article.title && (
                                <h3 className="grid__title typo--ml ff--primary ls-second-item">
                                  {decodeHTMLEntities(article.title)}
                                </h3>
                              )}
                              {article.link && (
                                <div className="grid__fauxlink fauxlink">
                                  <div className="fauxlink__inner">
                                    <span className="fauxlink__copy">
                                      {decodeHTMLEntities(article.link.title)}
                                    </span>
                                    <svg
                                      className="fauxlink__icon"
                                      height="14"
                                      width="16"
                                      aria-hidden="true"
                                    >
                                      <use xlinkHref="#arrow" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>
                            {article.image && (
                              <div className="grid__visual">
                                <img
                                  className="grid__image lazyload"
                                  src="/frontend/images/empty.png"
                                  width={getImageAttribute(
                                    article.image,
                                    "grid-s",
                                    "width"
                                  )}
                                  height={getImageAttribute(
                                    article.image,
                                    "grid-s",
                                    "height"
                                  )}
                                  alt={
                                    article.image.alt_text
                                      ? article.image.alt_text
                                      : ""
                                  }
                                  data-sizes="auto"
                                  data-src={getImageAttribute(
                                    article.image,
                                    "grid-s",
                                    "source_url"
                                  )}
                                  data-srcset={`${getImageAttribute(
                                    article.image,
                                    "grid-s",
                                    "source_url"
                                  )} 440w,
                        ${getImageAttribute(
                          article.image,
                          "grid-m",
                          "source_url"
                        )} 880w,
                        ${getImageAttribute(
                          article.image,
                          "grid-l",
                          "source_url"
                        )} 1540w`}
                                />
                              </div>
                            )}
                          </>
                        );
                        return (
                          <li
                            className={`grid__item grid__item--${
                              article.image ? "image" : "text"
                            } ls-item`}
                          >
                            {article.link ? (
                              link(
                                article.link,
                                {
                                  classname: `grid__link grid__inner${
                                    article.image
                                      ? " ls-reversed border-radius--s"
                                      : ""
                                  }`,
                                },
                                gridItemInner
                              )
                            ) : (
                              <div
                                className={`grid__inner${
                                  article.image
                                    ? " ls-reversed border-radius--s"
                                    : ""
                                }`}
                              >
                                {gridItemInner}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </section>
            );
          })
        : nothing(),
    ]).map((u) => u(state))
  )(
    get_related_articles(
      page.extra_data.post.brochures_taxonomy,
      page.extra_data.post.id
    )
  ).run(() => {});

export const get_related_articles = (
  topic: number[],
  pageId: number,
  size: number = 3
): AsyncState<Array<RelatedArticlesBlockItem>> =>
  loadingAsyncState(() =>
    WordPress.api<WordPress.NewsArticleResponse>()
      .select("article")
      .where({
        per_page: size,
        brochures_taxonomy: topic,
        exclude: pageId,
        orderby: 'rand'
      })
      .getMultiple()
      .then((d) => {
        if (d.status != 200) return { kind: "failed", status: d.status };
        return {
          kind: "result",
          status: d.status,
          value: d.data.map(m => {
            let header = m.custom_blocks ? m.custom_blocks.filter(f => f.blockName === 'vidda/header')[0]?.attrs as Attributes : null
            const image = header && header.image ? header.image : m.featured_media_object
            return {
              title: m.title.rendered,
              link: m.link
                ? linkWrapper({
                    title: i18next.t("related_articles.link"),
                    url: m.link,
                  })
                : undefined,
              image: image ?? placeholderImage("")
            }
          }),
        };
      })
  );